import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { llmCalls as callsApi, promptSlots as slotsApi, type Artifact, type LlmCallLog, type AuditLog as AuditEntry, type ManualEditEntry } from '../api'
import { useArtifactPicker } from '../components/useArtifactPicker'

const TABS = [
  { key: 'calls', label: 'LLM calls' },
  { key: 'audit', label: 'Audit log' },
  { key: 'edits', label: 'Manual edits' },
] as const
// (Removed the 'Artifacts' tab — its functionality was a strict subset of
// the Studio Artifacts browser at /studio/artifacts, which has full search,
// sort, bulk actions, more filters, and pagination. Both pulled from the
// same /api/artifacts/ endpoint, so there was no unique data here.)

type TabKey = (typeof TABS)[number]['key']

export default function Observability() {
  const [tab, setTab] = useState<TabKey>('calls')
  return (
    <div className="flex h-full flex-col bg-[color:var(--me-surface)]">
      <div className="flex items-center gap-1 border-b me-border px-3 py-1.5">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-selected={tab === t.key ? 'true' : undefined}
            className="me-tab"
          >
            {t.label}
          </button>
        ))}
        <span className="ml-auto text-[10.5px] me-text-mute italic">
          Real DAG runs only — sandbox / test-set / pipeline-test runs live in
          Prompts → Recent runs.
        </span>
      </div>
      <div className="me-scroll flex-1 overflow-auto p-5">
        {tab === 'calls' && <CallsTab />}
        {tab === 'audit' && <AuditTab />}
        {tab === 'edits' && <ManualEditsTab />}
      </div>
    </div>
  )
}

// ---------- LLM calls ----------

function CallsTab() {
  const slots = useQuery({ queryKey: ['promptSlots'], queryFn: () => slotsApi.list() })
  const [slotId, setSlotId] = useState<number | ''>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  // Structural per-artifact filter: when set, narrows to LLM calls FOR
  // this artifact (exact FK match, not substring text). Cleaner than
  // typing the id into the search box for "trace this artifact's journey".
  const [artifactPick, setArtifactPick] = useState<Artifact | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const pickerInputRef = useRef<HTMLInputElement | null>(null)
  const { filter: artFilter, setFilter: setArtFilter, artifacts: artCandidates } = useArtifactPicker({ limit: 30 })
  const [page, setPage] = useState(0)  // 0-indexed page number
  const PAGE_SIZE = 50
  const [selected, setSelected] = useState<LlmCallLog | null>(null)

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 250)
    return () => window.clearTimeout(t)
  }, [search])
  // Reset to page 0 whenever filters change so the user doesn't end
  // up on page 7 of a freshly-filtered shorter list.
  useEffect(() => { setPage(0) }, [slotId, statusFilter, debouncedSearch, artifactPick?.id])

  const params: Record<string, string | number> = {
    limit: PAGE_SIZE, offset: page * PAGE_SIZE,
  }
  if (slotId !== '') params.slot = slotId
  if (statusFilter) params.status = statusFilter
  if (debouncedSearch) params.search = debouncedSearch
  if (artifactPick) params.artifact_id = artifactPick.id
  const calls = useQuery({
    queryKey: ['llmCalls', { slotId, statusFilter, debouncedSearch, artifactId: artifactPick?.id, page }],
    queryFn: () => callsApi.listPaged(params),
    placeholderData: (prev) => prev,
  })
  const rows = calls.data?.results ?? []
  const total = calls.data?.count ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div className="grid grid-cols-[1fr_360px] gap-3">
      <div>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {/* Dedicated artifact-trace filter — bordered pill so it's clearly
              distinct from the free-text search. Always-visible label, with
              an "active" state that highlights the whole pill when set. */}
          <div
            className="relative inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px]"
            style={artifactPick ? {
              borderColor: 'var(--me-fresh, #16a34a)',
              background: 'rgba(34,197,94,0.10)',
            } : {
              borderColor: 'var(--me-border)',
              background: 'var(--me-panel)',
            }}
          >
            <span className="me-text-mute uppercase tracking-wider text-[9.5px]">Artifact</span>
            {artifactPick ? (
              <>
                <span className="me-mono text-[11px]">{artifactPick.composite_id}</span>
                <span className="truncate max-w-[180px] text-[11px] me-text-dim">
                  {artifactPick.subject || '(no subject)'}
                </span>
                <button
                  onClick={() => { setArtifactPick(null); setArtFilter(''); setPickerOpen(false) }}
                  className="ml-0.5 px-1 hover:opacity-70"
                  title="Clear artifact filter"
                  aria-label="Clear"
                >✕</button>
              </>
            ) : (
              <input
                ref={pickerInputRef}
                value={artFilter}
                onChange={(e) => { setArtFilter(e.target.value); setPickerOpen(true) }}
                onFocus={() => setPickerOpen(true)}
                onBlur={() => setTimeout(() => setPickerOpen(false), 150)}
                placeholder="type id or subject…"
                className="bg-transparent border-0 outline-none text-[12px] w-[180px]"
              />
            )}
            {pickerOpen && !artifactPick && artCandidates.length > 0 && (
              <ul
                className="absolute left-0 top-full z-20 mt-1 max-h-72 w-[400px] overflow-auto rounded-md border me-border shadow-md"
                style={{ background: 'var(--me-surface)' }}
              >
                {artCandidates.slice(0, 20).map((a) => (
                  <li
                    key={a.id}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      setArtifactPick(a)
                      setPickerOpen(false)
                      setArtFilter('')
                    }}
                    className="cursor-pointer px-2 py-1.5 text-[11.5px] hover:bg-[color:var(--me-hover)] border-b me-border last:border-b-0"
                  >
                    <div className="me-mono text-[10px] me-text-mute">{a.composite_id}</div>
                    <div className="truncate">{a.subject || '(no subject)'}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="search input + output + step + subject + error…"
            className="me-input flex-1 min-w-[200px] text-[12px] py-1.5"
          />
          <select
            value={slotId}
            onChange={(e) => setSlotId(e.target.value ? Number(e.target.value) : '')}
            className="me-input w-auto text-[12px] py-1.5"
          >
            <option value="">All slots</option>
            {(slots.data || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="me-input w-auto text-[12px] py-1.5"
          >
            <option value="">Any status</option>
            <option value="success">success</option>
            <option value="error">error</option>
            <option value="timeout">timeout</option>
          </select>
          <span className="me-mono text-[11px] me-text-mute">
            {total.toLocaleString()} total · page {page + 1}/{totalPages}
          </span>
        </div>
        <div className="mb-3 flex items-center gap-1">
          <button
            onClick={() => setPage(0)}
            disabled={page === 0}
            className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 disabled:opacity-40"
            title="First page"
          >« first</button>
          <button
            onClick={() => setPage(Math.max(0, page - 10))}
            disabled={page === 0}
            className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 disabled:opacity-40"
            title="Back 10 pages"
          >‹‹ -10</button>
          <button
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
            className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 disabled:opacity-40"
            title="Previous page"
          >‹ prev</button>
          <button
            onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
            disabled={page >= totalPages - 1}
            className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 disabled:opacity-40"
            title="Next page"
          >next ›</button>
          <button
            onClick={() => setPage(Math.min(totalPages - 1, page + 10))}
            disabled={page >= totalPages - 1}
            className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 disabled:opacity-40"
            title="Forward 10 pages"
          >+10 ››</button>
          <button
            onClick={() => setPage(totalPages - 1)}
            disabled={page >= totalPages - 1}
            className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 disabled:opacity-40"
            title="Last page"
          >last »</button>
          <span className="ml-auto text-[11px] me-text-mute italic">
            real DAG runs only — sandbox / test-set runs go to <code>/api/sandbox-runs/</code>
          </span>
        </div>
        {/* Render-issues banner: how many rows on THIS page have prompt-render
            issues (unrendered {{var}}, stray single brace, triple brace). Bright
            warning chip so it's hard to miss when a prompt is silently broken. */}
        {(() => {
          const withIssues = rows.filter((r) => (r.render_issues || []).length > 0)
          if (withIssues.length === 0) return null
          const totalIssues = withIssues.reduce((sum, r) => sum + (r.render_issues || []).length, 0)
          return (
            <div className="mb-3 me-chip me-chip-urgent inline-flex items-center gap-1.5">
              <span>⚠</span>
              <span>
                {withIssues.length} row{withIssues.length === 1 ? '' : 's'} on this page have
                prompt-render issues ({totalIssues} total)
              </span>
              <button
                onClick={() => setSelected(withIssues[0])}
                className="ml-1 underline cursor-pointer"
                title="Jump to the first affected row"
              >
                show first
              </button>
            </div>
          )
        })()}
        <div className="me-card overflow-auto">
          <table className="w-full text-[12px]">
            <thead className="me-text-mute">
              <tr className="border-b me-border">
                <th className="px-3 py-2 text-left font-normal text-[10.5px] uppercase tracking-wider">When</th>
                <th className="px-3 py-2 text-left font-normal text-[10.5px] uppercase tracking-wider">Step</th>
                <th className="px-3 py-2 text-left font-normal text-[10.5px] uppercase tracking-wider">Artifact</th>
                <th className="px-3 py-2 text-left font-normal text-[10.5px] uppercase tracking-wider">From</th>
                <th className="px-3 py-2 text-left font-normal text-[10.5px] uppercase tracking-wider">Status</th>
                <th className="px-3 py-2 text-right font-normal text-[10.5px] uppercase tracking-wider">In</th>
                <th className="px-3 py-2 text-right font-normal text-[10.5px] uppercase tracking-wider">Out</th>
                <th className="px-3 py-2 text-right font-normal text-[10.5px] uppercase tracking-wider">ms</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setSelected(r)}
                  className="cursor-pointer border-t me-border hover:bg-[color:var(--me-hover)]"
                  data-selected={selected?.id === r.id ? 'true' : undefined}
                >
                  <td className="me-mono px-3 py-1.5 me-text-dim whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="me-mono px-3 py-1.5 whitespace-nowrap">{r.step_name || '—'}</td>
                  <td className="px-3 py-1.5 max-w-[340px]">
                    {r.artifact ? (
                      <div className="flex flex-col">
                        <div className="flex items-center gap-1.5">
                          <span className="me-mono text-[10px] me-text-mute">{r.artifact_composite_id}</span>
                          {r.artifact_type && (
                            <span className="me-chip text-[9px] uppercase">{r.artifact_type}</span>
                          )}
                        </div>
                        <span className="truncate text-[11.5px] me-text">
                          {r.artifact_subject || `#${r.artifact}`}
                        </span>
                      </div>
                    ) : <span className="me-text-mute">—</span>}
                  </td>
                  <td className="me-mono px-3 py-1.5 text-[10.5px] me-text-dim truncate max-w-[180px]">
                    {r.artifact_from_email || '—'}
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-1">
                      <span className={r.status === 'success' ? 'me-chip me-chip-fresh' : 'me-chip me-chip-urgent'}>{r.status}</span>
                      {/* render_issues badge: prompt-render problems caught at
                          substitution time, independent of LLM success/failure.
                          Could be a "success" call that nevertheless had a
                          {{var}} silently leaked to the model. */}
                      {(r.render_issues || []).length > 0 && (
                        <span
                          className="me-chip me-chip-urgent text-[9px]"
                          title={r.render_issues.join('\n')}
                        >
                          ⚠ {r.render_issues.length} render
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="me-mono px-3 py-1.5 text-right">{r.input_tokens}</td>
                  <td className="me-mono px-3 py-1.5 text-right">{r.output_tokens}</td>
                  <td className="me-mono px-3 py-1.5 text-right">{r.latency_ms}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && <div className="p-4 text-[12px] me-text-mute">No LLM calls match.</div>}
        </div>
      </div>

      <aside className="me-card p-4 sticky top-0 self-start max-h-[calc(100vh-2rem)] overflow-auto">
        {selected ? (
          <>
            <div className="me-section-h">Call detail</div>
            <div className="me-mono mb-2 text-[11.5px]">
              {selected.model} · {selected.status} · {selected.latency_ms}ms · in={selected.input_tokens} out={selected.output_tokens}
            </div>
            {selected.artifact && <div className="me-mono text-[11px] me-text-mute">artifact #{selected.artifact}</div>}
            {selected.prompt_version && <div className="me-mono text-[11px] me-text-mute">prompt version #{selected.prompt_version}</div>}
            <div className="mt-3 me-section-h">Input</div>
            <pre className="me-mono whitespace-pre-wrap rounded-md bg-[color:var(--me-panel)] p-2 text-[11px] leading-relaxed max-h-64 overflow-auto">{selected.input || '(empty / redacted)'}</pre>
            <div className="mt-3 me-section-h">Output</div>
            <pre className="me-mono whitespace-pre-wrap rounded-md bg-[color:var(--me-panel)] p-2 text-[11px] leading-relaxed max-h-64 overflow-auto">{selected.output || '(empty / redacted)'}</pre>
            {selected.error_message && (
              <>
                <div className="mt-3 me-section-h">Error</div>
                <pre className="me-mono whitespace-pre-wrap text-[11px] text-[color:var(--me-urgent)]">{selected.error_message}</pre>
              </>
            )}
            {(selected.render_issues || []).length > 0 && (
              <>
                <div className="mt-3 me-section-h">⚠ Render issues ({selected.render_issues.length})</div>
                <ul className="me-mono text-[11px] text-[color:var(--me-urgent)] space-y-0.5 leading-snug">
                  {selected.render_issues.map((m, i) => (
                    <li key={i}>• {m}</li>
                  ))}
                </ul>
              </>
            )}
          </>
        ) : (
          <div className="text-[12px] me-text-mute">Click a row to inspect.</div>
        )}
      </aside>
    </div>
  )
}

// ---------- Audit log ----------

function AuditTab() {
  const [page, setPage] = useState(0)
  const PAGE_SIZE = 50
  const audit = useQuery({
    queryKey: ['audit', { page }],
    queryFn: async () => {
      const r = await fetch(`/api/audit-log/?limit=${PAGE_SIZE}&offset=${page * PAGE_SIZE}`, { credentials: 'include' })
      const data = await r.json()
      return { count: data.count as number, results: data.results as AuditEntry[] }
    },
    placeholderData: (prev) => prev,
  })
  const total = audit.data?.count ?? 0
  const rows = audit.data?.results ?? []
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  return (
    <div>
      <div className="mb-2 text-[11px] me-text-mute italic">
        System actions: prompt promotions, mark-stale operations, manual
        pipeline runs, ignore/unignore on artifacts, anything that mutates
        canonical state. User edits to AI-generated fields (matter names,
        summaries, etc.) are tracked separately under Manual edits.
      </div>
      <div className="mb-3 flex items-center gap-1">
        <span className="me-mono text-[11px] me-text-mute mr-2">
          {total.toLocaleString()} entries · page {page + 1}/{totalPages}
        </span>
        <button onClick={() => setPage(0)} disabled={page === 0} className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 disabled:opacity-40">« first</button>
        <button onClick={() => setPage(Math.max(0, page - 10))} disabled={page === 0} className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 disabled:opacity-40">‹‹ -10</button>
        <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 disabled:opacity-40">‹ prev</button>
        <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 disabled:opacity-40">next ›</button>
        <button onClick={() => setPage(Math.min(totalPages - 1, page + 10))} disabled={page >= totalPages - 1} className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 disabled:opacity-40">+10 ››</button>
        <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1} className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 disabled:opacity-40">last »</button>
      </div>
      <div className="space-y-2">
        {rows.map((r) => <AuditCard key={r.id} entry={r} />)}
        {rows.length === 0 && (
          <div className="text-[12px] me-text-mute">No audit entries yet.</div>
        )}
      </div>
    </div>
  )
}

function AuditCard({ entry }: { entry: AuditEntry }) {
  // System-triggered actions (no user FK) get a clear "SYS" badge
  // instead of a confusing "?" so it's obvious why a row has no actor.
  const isSystem = !entry.user_username
  const label = isSystem ? 'SYS' : (entry.user_username || '').slice(0, 2).toUpperCase()
  return (
    <div className="me-card flex gap-3 p-3 border-l-4" style={{ borderLeftColor: 'var(--me-fg)' }}>
      <div
        className="grid h-8 w-8 shrink-0 place-items-center rounded-full me-mono text-[11px]"
        style={{
          background: isSystem ? 'var(--me-panel)' : 'var(--me-panel)',
          color: isSystem ? 'var(--me-text-mute)' : 'var(--me-text-dim)',
        }}
        title={isSystem ? 'System-triggered action (no user)' : `User: ${entry.user_username}`}
      >
        {label}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium me-text">{entry.user_username || 'system'}</span>
          <span className="me-chip">{entry.action}</span>
          <span className="me-mono ml-auto text-[10.5px] me-text-mute">{relativeTime(entry.created_at)}</span>
        </div>
        <div className="me-mono mt-1 text-[11px] me-text-mute">
          {entry.target_type}#{entry.target_id}
        </div>
        {entry.note && <div className="mt-1 text-[12px] me-text-dim italic">{entry.note}</div>}
        {(entry.old_value || entry.new_value) && (
          <details className="mt-1 me-mono text-[10.5px] me-text-mute">
            <summary className="cursor-pointer">old / new</summary>
            <div className="mt-1 grid grid-cols-2 gap-2">
              <pre className="rounded-md bg-[color:var(--me-panel)] p-2 whitespace-pre-wrap">old: {JSON.stringify(entry.old_value, null, 2)}</pre>
              <pre className="rounded-md bg-[color:var(--me-panel)] p-2 whitespace-pre-wrap">new: {JSON.stringify(entry.new_value, null, 2)}</pre>
            </div>
          </details>
        )}
      </div>
    </div>
  )
}

function relativeTime(iso: string): string {
  const d = new Date(iso)
  const secs = (Date.now() - d.getTime()) / 1000
  if (secs < 60) return `${Math.round(secs)}s ago`
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`
  return `${Math.round(secs / 86400)}d ago`
}

// ---------- Manual edits ----------
//
// Every user edit to an AI-generated field (matter name, summary,
// narrative-group label/summary/notes, artifact ai_summary, etc.)
// records a ManualEdit row. This tab is the audit trail — useful for:
//   - tracking which AI choices users keep correcting (signal for prompt work)
//   - reverting an unintended edit (manual SQL for now; UI undo is XIII follow-up)
//   - feeding edits back to the AI on next run (XIII.D)

function ManualEditsTab() {
  const [page, setPage] = useState(0)
  const [target, setTarget] = useState<'all' | 'matter' | 'artifact'>('all')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const PAGE_SIZE = 50
  useEffect(() => { setPage(0) }, [target, debouncedSearch])
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 250)
    return () => window.clearTimeout(t)
  }, [search])

  const params: Record<string, string | number> = {
    limit: PAGE_SIZE, offset: page * PAGE_SIZE,
  }
  if (target !== 'all') params.target_type = target
  if (debouncedSearch) params.search = debouncedSearch
  const edits = useQuery({
    queryKey: ['manualEdits', { page, target, debouncedSearch }],
    queryFn: async () => {
      const r = await fetch('/api/manual-edits/?' + new URLSearchParams(
        Object.entries(params).map(([k, v]) => [k, String(v)])
      ), { credentials: 'include' })
      const data = await r.json()
      return { count: data.count as number, results: data.results as ManualEditEntry[] }
    },
    placeholderData: (prev) => prev,
    // Auto-refresh: keep the audit log fresh while it's open. 15s is
    // light on the server (this user is on a small set of recent rows)
    // and matches how operators normally watch the workspace inbox.
    refetchInterval: 15_000,
  })
  const total = edits.data?.count ?? 0
  const rows = edits.data?.results ?? []
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div>
      <div className="mb-2 text-[11px] me-text-mute italic">
        Every user edit to an AI-generated field (matter name, summary,
        stage, narrative groups, artifact subject + summary). Drives both
        the audit trail AND the prompt context — narrate / summarize /
        matter-summary all see recent edits and aim to preserve user phrasing.
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="search by composite id (M-025, MOVU-GOOG-EMAL-...), title/subject, field name…"
          className="me-input flex-1 min-w-[260px] text-[12px] py-1.5"
        />
        <select
          value={target}
          onChange={(e) => setTarget(e.target.value as typeof target)}
          className="me-input w-auto text-[12px] py-1.5"
        >
          <option value="all">All targets</option>
          <option value="matter">Matter</option>
          <option value="artifact">Artifact</option>
        </select>
        <button
          onClick={() => edits.refetch()}
          className="me-btn me-btn-outline !text-[10.5px] !px-2 !py-1 inline-flex items-center gap-1"
          title="Refresh now (auto-refreshes every 15s)"
          disabled={edits.isFetching}
        >
          {edits.isFetching ? '⟳ refreshing…' : '⟳ refresh'}
        </button>
        <span className="me-mono text-[11px] me-text-mute">
          {total.toLocaleString()} edits · page {page + 1}/{totalPages}
        </span>
      </div>
      <div className="mb-3 flex items-center gap-1">
        <button onClick={() => setPage(0)} disabled={page === 0} className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 disabled:opacity-40">« first</button>
        <button onClick={() => setPage(Math.max(0, page - 10))} disabled={page === 0} className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 disabled:opacity-40">‹‹ -10</button>
        <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 disabled:opacity-40">‹ prev</button>
        <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 disabled:opacity-40">next ›</button>
        <button onClick={() => setPage(Math.min(totalPages - 1, page + 10))} disabled={page >= totalPages - 1} className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 disabled:opacity-40">+10 ››</button>
        <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1} className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 disabled:opacity-40">last »</button>
      </div>
      <ul className="space-y-1.5">
        {rows.map((r) => <ManualEditCard key={r.id} entry={r} />)}
        {rows.length === 0 && (
          <div className="text-[12px] me-text-mute italic">No manual edits yet.</div>
        )}
      </ul>
    </div>
  )
}

/** Word-level diff: split both strings into space-respecting tokens
 *  and use a simple LCS-ish O(n*m) approach. Returns spans tagged
 *  'eq' | 'add' | 'del'. Bounded at 800 tokens per side to keep the
 *  matrix small for huge summary edits. */
function diffWords(oldText: string, newText: string): Array<{ kind: 'eq' | 'add' | 'del'; text: string }> {
  const tokenize = (s: string) => s.split(/(\s+)/).filter((t) => t !== '')
  const a = tokenize(oldText || '').slice(0, 800)
  const b = tokenize(newText || '').slice(0, 800)
  // LCS table.
  const dp: number[][] = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0))
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1])
    }
  }
  const out: Array<{ kind: 'eq' | 'add' | 'del'; text: string }> = []
  let i = 0, j = 0
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) { out.push({ kind: 'eq', text: a[i] }); i++; j++ }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ kind: 'del', text: a[i] }); i++ }
    else { out.push({ kind: 'add', text: b[j] }); j++ }
  }
  while (i < a.length) { out.push({ kind: 'del', text: a[i++] }) }
  while (j < b.length) { out.push({ kind: 'add', text: b[j++] }) }
  return out
}

type DiffMode = 'inline' | 'split'
const DIFF_MODE_KEY = 'me_manual_edit_diff_mode'
function loadDiffMode(): DiffMode {
  try {
    const v = localStorage.getItem(DIFF_MODE_KEY)
    return v === 'split' ? 'split' : 'inline'
  } catch { return 'inline' }
}
function saveDiffMode(m: DiffMode) {
  try { localStorage.setItem(DIFF_MODE_KEY, m) } catch { /* ignore */ }
}

function DiffView({ oldVal, newVal }: { oldVal: unknown; newVal: unknown }) {
  const oldS = typeof oldVal === 'string' ? oldVal : (oldVal == null ? '' : JSON.stringify(oldVal, null, 2))
  const newS = typeof newVal === 'string' ? newVal : (newVal == null ? '' : JSON.stringify(newVal, null, 2))
  const [mode, setMode] = useState<DiffMode>(loadDiffMode)
  const setModeAndSave = (m: DiffMode) => { setMode(m); saveDiffMode(m) }
  const tokens = useMemo(() => diffWords(oldS, newS), [oldS, newS])
  return (
    <div>
      {/* Tabs: Inline (red+green in one stream) vs Side-by-side
       *  (left = old with deletions colored, right = new with
       *  additions colored). Choice persists across views via
       *  localStorage. */}
      <div className="mb-1 inline-flex overflow-hidden rounded-sm border me-border">
        {(['inline', 'split'] as const).map((m) => (
          <button
            key={m}
            onClick={(e) => { e.stopPropagation(); setModeAndSave(m) }}
            data-selected={mode === m}
            className="me-btn !rounded-none !text-[10px] !px-2 !py-1"
            style={mode === m ? { background: 'var(--me-selected)', color: 'var(--me-text)', fontWeight: 600 } : {}}
            type="button"
          >
            {m === 'inline' ? 'Inline' : 'Side-by-side'}
          </button>
        ))}
      </div>
      {mode === 'inline' ? (
        <div
          className="me-mono text-[10.5px] leading-relaxed rounded-md bg-[color:var(--me-panel)] p-2 max-h-72 overflow-auto"
          style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          {tokens.map((t, i) => {
            if (t.kind === 'eq') return <span key={i}>{t.text}</span>
            if (t.kind === 'add') return (
              <span key={i} style={{ background: 'rgba(34,197,94,0.20)', color: 'var(--me-fresh, #16a34a)' }}>{t.text}</span>
            )
            return (
              <span key={i} style={{ background: 'rgba(239,68,68,0.18)', color: 'var(--me-stale, #dc2626)', textDecoration: 'line-through' }}>{t.text}</span>
            )
          })}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="me-section-h !mb-0.5 text-[10px]">Before</div>
            <div
              className="me-mono text-[10.5px] leading-relaxed rounded-md bg-[color:var(--me-panel)] p-2 max-h-72 overflow-auto"
              style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {tokens.map((t, i) => {
                if (t.kind === 'add') return null  // additions don't appear on the 'before' side
                if (t.kind === 'eq') return <span key={i}>{t.text}</span>
                return (
                  <span key={i} style={{ background: 'rgba(239,68,68,0.18)', color: 'var(--me-stale, #dc2626)', textDecoration: 'line-through' }}>{t.text}</span>
                )
              })}
            </div>
          </div>
          <div>
            <div className="me-section-h !mb-0.5 text-[10px]">After</div>
            <div
              className="me-mono text-[10.5px] leading-relaxed rounded-md bg-[color:var(--me-panel)] p-2 max-h-72 overflow-auto"
              style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {tokens.map((t, i) => {
                if (t.kind === 'del') return null  // deletions don't appear on the 'after' side
                if (t.kind === 'eq') return <span key={i}>{t.text}</span>
                return (
                  <span key={i} style={{ background: 'rgba(34,197,94,0.20)', color: 'var(--me-fresh, #16a34a)' }}>{t.text}</span>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ManualEditCard({ entry }: { entry: ManualEditEntry }) {
  const [expanded, setExpanded] = useState(false)
  const targetUrl = entry.target_type === 'matter'
    ? `/workspace?matter=${entry.target_id}`
    : `/studio/artifact/${entry.target_id}`
  return (
    <li
      // Whole card toggles expand on click; the title <a> stops
      // propagation so it navigates instead. cursor-pointer hints
      // the row is clickable.
      onClick={() => setExpanded(!expanded)}
      className="me-card p-2.5 cursor-pointer"
    >
      <div className="flex items-center gap-2 text-[12px]">
        <span
          aria-label={expanded ? 'Collapse' : 'Expand'}
          className="me-text-mute shrink-0"
          title={expanded ? 'Hide diff' : 'Show diff'}
        >
          {expanded ? '▾' : '▸'}
        </span>
        <span className="me-chip">{entry.target_type}</span>
        <span className="me-mono me-text-mute text-[11px]">{entry.field}</span>
        <a
          href={targetUrl} target="_blank" rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="font-medium me-text truncate min-w-0 hover:underline max-w-[60%]"
          title={`Open ${entry.target_type} (new tab)`}
        >
          {entry.target_label || `#${entry.target_id}`}
        </a>
        <span className="ml-auto me-mono text-[10.5px] me-text-mute shrink-0">
          {entry.user_username || 'system'} · {relativeTime(entry.created_at)}
        </span>
      </div>
      {entry.reason && (
        <div className="mt-1 ml-5 text-[11.5px] me-text-dim italic">
          “{entry.reason}”
        </div>
      )}
      {expanded && (
        <div className="mt-2 ml-5" onClick={(e) => e.stopPropagation()}>
          <div className="me-section-h !mb-0.5">Diff (old → new)</div>
          <DiffView oldVal={entry.old_value} newVal={entry.new_value} />
        </div>
      )}
    </li>
  )
}
